import axios from "../axios";

const API_PATH = "/prompts";

/** See Prompt.java for doc notes */
export class Prompt {
  id;
  name;
  content;
  description;
  isUniversal;
  organizationTenantIds;
  /** see PromptRules.java for doc notes */
  rules;
};

const promptApi = {
  /**
   * Get all prompts accessible by the current user's organization
   * @returns {Promise<Array<Prompt>>} List of prompts
   */
  getPrompts() {
    return axios.get(API_PATH).then(({ data }) => data);
  },

  /**
   * Get all prompts regardless of organization (admin only)
   * @returns {Promise<Array<Prompt>>} List of all prompts
   */
  getAllPrompts() {
    return axios.get(`${API_PATH}/admin`).then(({ data }) => data);
  },

  /**
   * Get prompts filtered by contextual rules
   * @param {Object} filters
   * @param {string} filters.location - "disclosure" | "ai-draft" | "chat"
   * @param {string} filters.page - Route path relative to /app/
   * @param {string} filters.stage - Question stage
   * @param {string} filters.completeness - Completeness level
   * @param {string} filters.confidence - Confidence level
   * @returns {Promise<Array<Prompt>>} List of matching prompts
   */
  getFilteredPrompts({ location, page, stage, completeness, confidence } = {}) {
    const params = new URLSearchParams();
    if (location) params.append("location", location);
    if (page) params.append("page", page);
    if (stage) params.append("stage", stage);
    if (completeness) params.append("completeness", completeness);
    if (confidence) params.append("confidence", confidence);
    const query = params.toString();
    return axios.get(`${API_PATH}/filtered${query ? `?${query}` : ""}`).then(({ data }) => data);
  },

  /**
   * Create a new prompt (admin only)
   * @param {Object} data - The prompt data
   * @param {string} data.name - Prompt name
   * @param {string} data.content - Prompt content
   * @param {string} data.description - Prompt description
   * @param {boolean} data.isUniversal - Whether prompt is universal
   * @param {Array<string>} data.organizationTenantIds - Organization tenant IDs
   * @returns {Promise<Object>} Created prompt
   */
  createPrompt(data) {
    return axios.post(API_PATH, data).then(({ data }) => data);
  },

  /**
   * Update an existing prompt (admin only)
   * @param {string} id - Prompt ID
   * @param {Object} data - The prompt data
   * @returns {Promise<Object>} Updated prompt
   */
  updatePrompt({ id, data }) {
    return axios.put(`${API_PATH}/${id}`, data).then(({ data }) => data);
  },

  /**
   * Delete a prompt (admin only)
   * @param {string} id - Prompt ID
   * @returns {Promise<void>}
   */
  deletePrompt(id) {
    return axios.delete(`${API_PATH}/${id}`).then(({ data }) => data);
  },
};

export default promptApi;
